import { scopeThreadRef, scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ProjectContentMatch, ScopedThreadRef } from "@cmmd-forge/contracts";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { useEnvironmentApi } from "~/environmentApi";
import { readCmmdForgeRuntimeListSnapshot } from "~/cmmd/cmmdForgeRuntimeListResource";
import { listSavedEnvironmentRecords } from "~/environments/runtime";
import { projectSearchContentsQueryOptions } from "~/lib/projectReactQuery";
import { runThreadExecutionIntentAction } from "~/lib/runThreadExecutionIntentAction";
import { readThreadCmmdProjectId } from "~/lib/threadCmmdProjectId";
import { newCommandId } from "~/lib/utils";
import {
  useProjectContentSearchStore,
  type ProjectContentSearchTarget,
} from "~/projectContentSearchStore";
import { useRightPanelStore } from "~/rightPanelStore";
import { useCloudRuntimeProjectHandoffStore } from "../sidebar/cloudRuntimeProjectHandoffStore";

import {
  clampProjectContentSearchSelection,
  createProjectContentSearchPresentation,
  nextProjectContentSearchSelection,
  previousProjectContentSearchSelection,
} from "./projectContentSearchDialog.logic";
import { resolveProjectContentSearchConnectionEnvironment } from "./projectContentSearchConnection";
import {
  ProjectContentSearchBody,
  ProjectContentSearchControls,
  ProjectContentSearchStatus,
} from "./ProjectContentSearchDialogView";

const CONTENT_SEARCH_DEBOUNCE_MS = 180;

type ProjectContentSearchConnectionState =
  | { readonly kind: "ready" }
  | { readonly kind: "connecting" }
  | { readonly kind: "failed" };

export function ProjectContentSearchDialog(props: {
  readonly onOpenChange: (open: boolean) => void;
  readonly onOpenMatch: (match: ProjectContentMatch, threadRef: ScopedThreadRef) => void;
  readonly target: ProjectContentSearchTarget;
}) {
  const [query, setQuery] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [connectionState, setConnectionState] = useState<ProjectContentSearchConnectionState>({
    kind: "ready",
  });
  const [searchEnvironmentId, setSearchEnvironmentId] = useState(props.target.environmentId);
  const selectedResultRef = useRef<HTMLButtonElement | null>(null);
  const environmentApi = useEnvironmentApi(searchEnvironmentId);
  const [debouncedQuery, queryDebouncer] = useDebouncedValue(
    query,
    { wait: CONTENT_SEARCH_DEBOUNCE_MS },
    (state) => ({ isPending: state.isPending }),
  );
  const search = useQuery(
    projectSearchContentsQueryOptions({
      environmentId: searchEnvironmentId,
      cwd: props.target.cwd,
      query: debouncedQuery,
      caseSensitive,
      wholeWord,
      useRegex,
      enabled: environmentApi !== undefined,
    }),
  );
  const presentation = createProjectContentSearchPresentation({
    connected: environmentApi !== undefined,
    data: search.data,
    debouncedQuery,
    debouncePending: queryDebouncer.state.isPending,
    error: search.error,
    isFetching: search.isFetching,
    query,
  });
  const selectedMatchIndex = clampProjectContentSearchSelection(
    selectedIndex,
    presentation.matches.length,
  );
  const searchThreadRef = scopeThreadRef(searchEnvironmentId, props.target.threadRef.threadId);

  useEffect(() => {
    selectedResultRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedMatchIndex]);

  const resetSelection = (): void => setSelectedIndex(0);
  const connectEnvironment = async (): Promise<void> => {
    setConnectionState({ kind: "connecting" });
    try {
      await runThreadExecutionIntentAction(
        props.target.environmentId,
        {
          action: "files",
          projectId: readThreadCmmdProjectId(props.target.threadRef),
          targetId: null,
          dispatchIdentity: newCommandId(),
        },
        { dispatch: async () => undefined },
      );
      setSearchEnvironmentId(
        resolveProjectContentSearchConnectionEnvironment({
          environmentId: props.target.environmentId,
          catalogRows: readCmmdForgeRuntimeListSnapshot().rows,
          handoffs: Object.values(useCloudRuntimeProjectHandoffStore.getState().byRuntimeId),
          savedEnvironments: listSavedEnvironmentRecords(),
        }),
      );
      setConnectionState({ kind: "ready" });
    } catch {
      setConnectionState({ kind: "failed" });
    }
  };
  const openSelectedMatch = (): void => {
    if (!presentation.canOpenMatches) return;
    const match = presentation.matches[selectedMatchIndex];
    if (match) props.onOpenMatch(match, searchThreadRef);
  };
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const nextSelection =
      event.key === "ArrowDown"
        ? nextProjectContentSearchSelection(selectedIndex, presentation.matches.length)
        : event.key === "ArrowUp"
          ? previousProjectContentSearchSelection(selectedIndex, presentation.matches.length)
          : null;
    if (nextSelection !== null) {
      event.preventDefault();
      setSelectedIndex(nextSelection);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      openSelectedMatch();
    }
  };

  return (
    <Dialog onOpenChange={props.onOpenChange} open>
      <DialogPopup
        className="max-h-[min(100dvh-3rem,45rem)] max-w-3xl overflow-hidden p-0"
        viewportClassName="max-sm:pt-12"
      >
        <DialogHeader className="shrink-0 gap-3 border-b border-border px-4 pb-3 pt-4 md:px-6 md:pt-5">
          <div className="min-w-0 pr-10">
            <DialogTitle className="truncate text-base">Search project files</DialogTitle>
            <DialogDescription className="truncate">{props.target.projectName}</DialogDescription>
          </div>
          <ProjectContentSearchControls
            caseSensitive={caseSensitive}
            onCaseSensitiveChange={(pressed) => {
              resetSelection();
              setCaseSensitive(pressed);
            }}
            onKeyDown={handleSearchKeyDown}
            onQueryChange={(nextQuery) => {
              resetSelection();
              setQuery(nextQuery);
            }}
            onUseRegexChange={(pressed) => {
              resetSelection();
              setUseRegex(pressed);
            }}
            onWholeWordChange={(pressed) => {
              resetSelection();
              setWholeWord(pressed);
            }}
            query={query}
            useRegex={useRegex}
            wholeWord={wholeWord}
          />
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          <ProjectContentSearchStatus status={presentation.status} />
          <ProjectContentSearchBody
            connectionAction={
              connectionState.kind === "connecting"
                ? { kind: "connecting" }
                : connectionState.kind === "failed"
                  ? { kind: "failed", onConnect: connectEnvironment }
                  : { kind: "ready", onConnect: connectEnvironment }
            }
            onOpenMatch={(match) => {
              if (presentation.canOpenMatches) props.onOpenMatch(match, searchThreadRef);
            }}
            onSelect={setSelectedIndex}
            presentation={presentation}
            selectedIndex={selectedMatchIndex}
            selectedResultRef={selectedResultRef}
          />
        </div>
      </DialogPopup>
    </Dialog>
  );
}

export function ProjectContentSearchOverlay() {
  const target = useProjectContentSearchStore((state) => state.target);
  const close = useProjectContentSearchStore((state) => state.close);
  if (target === null) return null;
  return (
    <ProjectContentSearchDialog
      key={`${target.environmentId}:${target.threadRef.threadId}:${target.cwd}`}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      onOpenMatch={(match, threadRef) => {
        useRightPanelStore
          .getState()
          .openFile(scopedThreadKey(threadRef), match.path, match.lineNumber, "connected-only");
        close();
      }}
      target={target}
    />
  );
}
